import { Injectable, inject, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, throwError, switchMap, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ProductDto, ProductUpdatePayload } from '../../api/models/product.dto';
import { AuthService } from '../core/auth.service';
import { PublicListingService } from './public-listing.service';

/**
 * PRODUCT LISTING SERVICE
 * Strictly maps to /api/Product endpoints per OpenAPI specification.
 *
 * Endpoint reference:
 *   GET    /api/Product/broker/{brokerId}    — admin scoped list
 *   GET    /api/Product/{id}                 — single product
 *   GET    /api/Product/sale/boat            — public boat sale products
 *   GET    /api/Product/sale/merchandise     — public merchandise sale products
 *   POST   /api/Product/add-product          — create listing
 *   PUT    /api/Product/edit-product/{id}    — update listing
 *   DELETE /api/Product/delete-product/{id} — delete listing
 */
@Injectable({ providedIn: 'root' })
export class ProductService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly publicListings = inject(PublicListingService);
  private readonly baseUrl = `${environment.apiUrl}/Product`;

  // ── Admin Workflows (Broker Scoped) ─────────────────────

  getProductsByBroker(staffId: any, brokerId: number): Observable<ProductDto[]> {
    if (!brokerId) return of([]);
    const cacheBuster = `_t=${Date.now()}`;
    return this.http
      .get<ProductDto[]>(`${this.baseUrl}/broker/${brokerId}/staff/${staffId}?${cacheBuster}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getSoldOutProducts(staffId: any, brokerId: number): Observable<ProductDto[]> {
    if (!brokerId) return of([]);
    const cacheBuster = `_t=${Date.now()}`;
    return this.http
      .get<ProductDto[]>(`${this.baseUrl}/broker/${brokerId}/staff/${staffId}/sold-out?${cacheBuster}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Public Workflows (Global) ───────────────────────────

  /** GET /api/Product/sale/boat */
  getSaleBoats(): Observable<ProductDto[]> {
    return this.http
      .get<ProductDto[]>(`${this.baseUrl}/sale/boat`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /** GET /api/Product/sale/merchandise */
  getSaleMerchandise(): Observable<ProductDto[]> {
    return this.http
      .get<ProductDto[]>(`${this.baseUrl}/sale/merchandise`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Shared / Specific Queries ───────────────────────────

  getProductById(id: string | number): Observable<ProductDto> {
    return this.http
      .get<ProductDto>(`${this.baseUrl}/${id}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  checkListingStatus(
    type: 'boat' | 'merchandise',
    itemId: number,
    brokerId: number,
    staffId: any,
  ): Observable<boolean> {
    if (!itemId || !brokerId) return of(false);
    return this.getProductsByBroker(staffId, brokerId).pipe(
      map((products) => {
        if (type === 'boat') {
          return products.some(
            (p: any) => p.productBoats && p.productBoats.some((pb: any) => pb.boatId === itemId),
          );
        } else {
          return products.some(
            (p: any) =>
              p.productMerchandises &&
              p.productMerchandises.some((pm: any) => pm.merchandiseId === itemId),
          );
        }
      }),
      catchError(() => of(false)),
    );
  }

  // ── Inventory Selection (GET-only addtolisting endpoints) ──────────────

  /**
   * Returns boats that can be added to a listing for this broker.
   * GET /api/Boat/broker/{brokerId}/addtolisting
   */
  getBoatsForListing(staffId: any, brokerId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${environment.apiUrl}/Boat/broker/${brokerId}/staff/${staffId}/addtolisting`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /**
   * Returns merchandise that can be added to a listing for this broker.
   * GET /api/Merchandise/broker/{brokerId}/addtolisting
   */
  getMerchandiseForListing(staffId: any, brokerId: number): Observable<any[]> {
    return this.http
      .get<
        any[]
      >(`${environment.apiUrl}/Merchandise/broker/${brokerId}/staff/${staffId}/addtolisting`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // ── Create & Update (Sales Configurator) ─────────────────

  /**
   * POST /api/Product/add-product
   * Always called for first-time listing creation.
   * Never call this when productId already exists — use updateProduct instead.
   */
  createProduct(payload: {
    brokerId: number;
    productTypeId: number;
    statusId: number;
    specialOfferId?: number | null;
    productCode?: string;
    isActive: boolean;
    modifiedBy?: number | null;
    staffIds?: number[];
    locations?: number[];
    boatId?: number | null;
    merchandiseId?: number | null;
    price: number;
    productName?: string;
  }): Observable<ProductDto> {
    if (isDevMode())
      console.log('[ProductService] createProduct POST /api/Product/add-product payload:', payload);
    return this.http
      .post<ProductDto>(`${this.baseUrl}/add-product`, payload)
      .pipe(tap(() => this.publicListings.invalidateCache()), catchError((err) => throwError(() => err)));
  }

  /**
   * PUT /api/Product/edit-product/{id}
   * Only called when editing an existing listing (productId is known).
   * Builds a clean payload with only OpenAPI-specified fields.
   */
  updateProduct(
    id: number,
    patch: Partial<ProductUpdatePayload>,
    existing?: ProductDto,
  ): Observable<ProductDto> {
    if (isDevMode())
      console.log(
        '[ProductService] updateProduct PUT /api/Product/edit-product/' + id + ' patch:',
        patch,
      );

    const obs$ =
      existing || (patch.statusId && patch.brokerId && patch.productTypeId)
        ? of(existing || ({} as ProductDto))
        : this.getProductById(id);

    return obs$.pipe(
      switchMap((existingDto) => {
        const base = structuredClone(existingDto || {});

        const currentUser = this.authService.currentUser();
        const activeBrokerId = this.authService.activeBrokerId();
        const modifiedByStaffId = currentUser?.staffId ? Number(currentUser.staffId) : null;

        // Build a clean payload matching only the OpenAPI AddProductRequest fields.
        // Never forward navigation collections (productBoats, productContacts, etc.) —
        // additionalProperties:false on the backend silently rejects unknown keys.
        const payload: ProductUpdatePayload = {
          productId: Number(id),
          brokerId: Number(patch.brokerId ?? activeBrokerId ?? base.brokerId),
          productTypeId: Number(patch.productTypeId ?? base.productTypeId),
          statusId: Number(patch.statusId ?? base.statusId),
          specialOfferId:
            patch.specialOfferId !== undefined
              ? patch.specialOfferId
              : (base.specialOfferId ?? null),
          productCode: patch.productCode ?? base.productCode ?? '',
          isActive: patch.isActive !== undefined ? patch.isActive : (base.isActive ?? true),
          modifiedBy: modifiedByStaffId || base.modifiedBy || null,
          price: Number(patch.price ?? base.price ?? 0),
          productName: patch.productName ?? base.productName ?? '',
          // Send flat ID arrays — the backend resolves junction tables server-side
          staffIds:
            patch.staffIds ??
            (base.productContacts || []).map((c: any) => Number(c.staffId)).filter(Boolean),
          locations:
            patch.locations ??
            (base.productLocations || []).map((l: any) => Number(l.locationId)).filter(Boolean),
          boatId: patch.boatId !== undefined ? patch.boatId : base.boatId || null,
          merchandiseId:
            patch.merchandiseId !== undefined ? patch.merchandiseId : base.merchandiseId || null,
        };

        // Validation
        if (!payload.statusId)
          throw new Error(`[ProductService] Missing statusId for product ${id}`);
        if (!payload.brokerId)
          throw new Error(`[ProductService] Missing brokerId for product ${id}`);
        if (!payload.productTypeId)
          throw new Error(`[ProductService] Missing productTypeId for product ${id}`);

        if (isDevMode())
          console.log(`[ProductService] PUT ${this.baseUrl}/edit-product/${id}`, payload);
        return this.http.put<void>(`${this.baseUrl}/edit-product/${id}`, payload).pipe(
          map(
            (): ProductDto => ({
              ...base,
              ...payload,
              productId: id,
            }),
          ),
          tap(() => {
            this.publicListings.invalidateCache();
            if (isDevMode()) console.log(`[ProductService] Update successful for ID ${id}`);
          }),
          catchError((err) => {
            console.error(`[ProductService] Error updating product ${id}:`, err);
            return throwError(() => err);
          }),
        );
      }),
      catchError((err) => throwError(() => err)),
    );
  }

  /**
   * DELETE /api/Product/{id}/staff/{staffId}
   */
  deleteProductWithStaff(productId: number, staffId: number): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/delete-product/${productId}/staff/${staffId}`)
      .pipe(tap(() => this.publicListings.invalidateCache()), catchError((err) => throwError(() => err)));
  }

  /**
   * POST /api/Product/update-status/{id}/staff/{staffId}/status/{statusId}
   */
  updateProductStatus(productId: number, staffId: number, statusId: number): Observable<void> {
    return this.http
      .post<void>(
        `${this.baseUrl}/update-status/${productId}/staff/${staffId}/status/${statusId}`,
        {},
      )
      .pipe(tap(() => this.publicListings.invalidateCache()), catchError((err) => throwError(() => err)));
  }
}
